import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Users, Building2, Download, MapPin, Calendar, DollarSign } from 'lucide-react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { calculateAge } from '@/components/utils/dynamicTaxCalculations';
import { downloadCSV } from '@/components/utils/pdfGeneration';
import { format } from 'date-fns';

export default function DemographicReports() {
  const [employers, setEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [demographics, setDemographics] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      calculateDemographics();
    }
  }, [selectedEmployer, employees, familyMembers]);

  const loadData = async () => {
    try {
      const [employersData, employeesData, familyData] = await Promise.all([
        api.entities.Employer.list(),
        api.entities.Employee.list(),
        api.entities.FamilyMember.list()
      ]);
      setEmployers(employersData);
      setEmployees(employeesData);
      setFamilyMembers(familyData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateDemographics = () => {
    const filtered = selectedEmployer === 'all' 
      ? employees 
      : employees.filter(e => e.employer_id === selectedEmployer);

    // Age Distribution
    const ageGroups = { '18-29': 0, '30-39': 0, '40-49': 0, '50-59': 0, '60+': 0 };
    filtered.forEach(emp => {
      if (emp.date_of_birth) {
        const age = calculateAge(emp.date_of_birth);
        if (age < 30) ageGroups['18-29']++;
        else if (age < 40) ageGroups['30-39']++;
        else if (age < 50) ageGroups['40-49']++;
        else if (age < 60) ageGroups['50-59']++;
        else ageGroups['60+']++;
      }
    });

    const ageDistribution = Object.keys(ageGroups).map(range => ({
      name: range,
      value: ageGroups[range]
    }));

    // Gender Distribution
    const genderCounts = { M: 0, F: 0, Unknown: 0 };
    filtered.forEach(emp => {
      if (emp.gender === 'M') genderCounts.M++;
      else if (emp.gender === 'F') genderCounts.F++;
      else genderCounts.Unknown++;
    });

    const genderDistribution = [
      { name: 'Male', value: genderCounts.M },
      { name: 'Female', value: genderCounts.F },
      { name: 'Not Specified', value: genderCounts.Unknown }
    ].filter(g => g.value > 0);

    // State Distribution
    const stateCounts = {};
    filtered.forEach(emp => {
      const state = emp.state_of_residence || emp.state || 'Unknown';
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    });

    const stateDistribution = Object.keys(stateCounts)
      .map(state => ({ name: state, value: stateCounts[state] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    // Family Size
    const familySizes = { '1': 0, '2': 0, '3': 0, '4': 0, '5+': 0 };
    filtered.forEach(emp => {
      const family = familyMembers.filter(f => f.employee_id === emp.id && f.is_active);
      const size = family.length + 1; // +1 for employee
      if (size === 1) familySizes['1']++;
      else if (size === 2) familySizes['2']++;
      else if (size === 3) familySizes['3']++;
      else if (size === 4) familySizes['4']++;
      else familySizes['5+']++;
    });

    const familyDistribution = Object.keys(familySizes).map(size => ({
      name: `${size} ${size === '1' ? 'person' : 'people'}`,
      value: familySizes[size]
    }));

    // Employment Status
    const employmentStatus = {
      full_time: filtered.filter(e => e.employment_status === 'full_time').length,
      part_time: filtered.filter(e => e.employment_status === 'part_time').length,
      contractor: filtered.filter(e => e.employment_status === 'contractor').length
    };

    const employmentDistribution = [
      { name: 'Full-Time', value: employmentStatus.full_time },
      { name: 'Part-Time', value: employmentStatus.part_time },
      { name: 'Contractor', value: employmentStatus.contractor }
    ].filter(e => e.value > 0);

    // Average Salary
    const salaries = filtered.filter(e => e.annual_salary > 0).map(e => e.annual_salary);
    const avgSalary = salaries.length > 0 ? salaries.reduce((a, b) => a + b, 0) / salaries.length : 0;
    const medianSalary = salaries.length > 0 ? salaries.sort((a, b) => a - b)[Math.floor(salaries.length / 2)] : 0;

    setDemographics({
      totalEmployees: filtered.length,
      ageDistribution,
      genderDistribution,
      stateDistribution,
      familyDistribution,
      employmentDistribution,
      avgSalary,
      medianSalary,
      avgAge: filtered.reduce((sum, e) => {
        if (e.date_of_birth) return sum + calculateAge(e.date_of_birth);
        return sum;
      }, 0) / filtered.length
    });
  };

  const exportDemographics = () => {
    const rows = [['Metric', 'Value']];
    rows.push(['Total Employees', demographics.totalEmployees]);
    rows.push(['Average Age', demographics.avgAge?.toFixed(1)]);
    rows.push(['Average Salary', `$${demographics.avgSalary?.toFixed(2)}`]);
    rows.push(['Median Salary', `$${demographics.medianSalary?.toFixed(2)}`]);
    rows.push(['', '']);
    rows.push(['Age Group', 'Count']);
    demographics.ageDistribution?.forEach(d => rows.push([d.name, d.value]));
    rows.push(['', '']);
    rows.push(['Gender', 'Count']);
    demographics.genderDistribution?.forEach(d => rows.push([d.name, d.value]));

    const csv = rows.map(row => row.join(',')).join('\n');
    downloadCSV(csv, `demographic_report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Demographic Reports</h1>
          <p className="text-slate-500 mt-1">
            Employee population analysis and insights
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedEmployer} onValueChange={setSelectedEmployer}>
            <SelectTrigger className="w-48">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Employers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employers</SelectItem>
              {employers.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>{emp.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={exportDemographics} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Employees</p>
                <p className="text-2xl font-bold">{demographics.totalEmployees}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Average Age</p>
                <p className="text-2xl font-bold">{demographics.avgAge?.toFixed(1)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg Salary</p>
                <p className="text-xl font-bold">${(demographics.avgSalary || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">States</p>
                <p className="text-2xl font-bold">{demographics.stateDistribution?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle>Age Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={demographics.ageDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardHeader>
              <CardTitle>Gender Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={demographics.genderDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label
                  >
                    {demographics.genderDistribution?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card>
            <CardHeader>
              <CardTitle>Family Size Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={demographics.familyDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card>
            <CardHeader>
              <CardTitle>Employment Status</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={demographics.employmentDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={(entry) => `${entry.name}: ${entry.value}`}
                  >
                    {demographics.employmentDistribution?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Geographic Distribution */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Card>
          <CardHeader>
            <CardTitle>Geographic Distribution (Top 10 States)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={demographics.stateDistribution} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis dataKey="name" type="category" width={60} stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}